import React, { useState, useEffect } from 'react';
import { AsyncPaginate } from 'react-select-async-paginate';
import { CFormLabel } from '@coreui/react';
import { useAuth } from '../contexts/AuthContext';
import { useProduct } from '../contexts/ProductContext';

function ProductSelection({ value, onChange, className, labelClassName, multiSelect = true, required = false, error = false, type = 'all' }) {
    const requiredIcon = required ? <span className="text-danger">*</span> : null;

    const { restaurantId } = useAuth();
    const { getAllProducts } = useProduct();

    const [totalRenderedRecords, setTotalRenderedRecords] = useState(0);
    const [allSelected, setAllSelected] = useState(false);
    const [filter, setFilter] = useState({
        limit: 50,
        filter: null,
        modifiers: null,
        categories: null,
        restaurant_id: restaurantId,
        totalRenderedRecords: 0,
        type: 'all',
    });

    const [key, setKey] = useState(0);

    useEffect(() => {
        if (restaurantId && type) {
            setFilter({ ...filter, restaurant_id: restaurantId, type });
            setAllSelected(false);
        }
    }, [restaurantId, type, allSelected]);

    useEffect(() => {
        setKey((prevKey) => prevKey + 1);
    }, [restaurantId, type, allSelected]);

    const fetchAllProducts = async () => {
        try {
            const response = await getAllProducts({ ...filter, limit: 0, totalRenderedRecords: 0 });
            return response.products.map((product) => ({
                label: product.name,
                value: product.id,
                type: product.type,
            }));
        } catch (error) {
            console.error('Error fetching all products', error);
            return [];
        }
    };

    const fetchProducts = async (searchValue, loadedOptions, { totalRenderedRecords }) => {
        try {
            if (restaurantId) {
                const response = await getAllProducts({ ...filter, filter: searchValue, totalRenderedRecords });

                if (response.totalPages > 0) {
                    let newOptions = response.products.map((product) => ({
                        label: product.name,
                        value: product.id,
                        type: product.type,
                    }));

                    const selectedValue = type !== 'all' ? value.filter((f) => f.type === type) : value;

                    let hasMore = totalRenderedRecords + newOptions.length < response.totalCount;

                    let options = [];

                    if (selectedValue.length < response.totalCount) {
                        options = [{ label: 'Select All', value: 'select_all' }, ...newOptions];
                    } else {
                        hasMore = false;
                        newOptions = [{ label: 'Remove All', value: 'remove_all' }];
                        options = [...newOptions];
                    }

                    return {
                        options: options.length > 0 ? options : [],
                        hasMore,
                        additional: { totalRenderedRecords: totalRenderedRecords + filter.limit },
                    };
                } else {
                    return { options: [], hasMore: false, additional: null };
                }
            }
        } catch (error) {
            console.error('Error fetching products', error);
            return { options: [], hasMore: false, additional: null };
        }
    };

    const handleOnChange = async (selectedOptions) => {
        if (selectedOptions && selectedOptions.some((option) => option.value === 'select_all')) {
            let productsToSelect = [];

            const allProducts = await fetchAllProducts(type);
            productsToSelect = [...value, ...allProducts];

            const uniqueSelectedOptions = Array.from(new Set(productsToSelect.map(option => option.value)))
                .map(value => productsToSelect.find(option => option.value === value));

            onChange(uniqueSelectedOptions.filter(option => option.value !== 'select_all'));
            setAllSelected(true);
        } else if (selectedOptions && selectedOptions.some((option) => option.value === 'remove_all')) {
            value = []
            onChange([]);
            setAllSelected(true);
        } else {
            setAllSelected(false);
            const uniqueSelectedOptions = Array.from(new Set(selectedOptions.map(option => option.value)))
                .map(value => selectedOptions.find(option => option.value === value));

            onChange(uniqueSelectedOptions);
        }
    };

    const customStyles = {
        option: (provided, state) => ({
            ...provided,
            whiteSpace: state.isFocused ? 'pre-wrap' : 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        }),
    };

    return (
        <div key={key} className={className}>
            <div className={labelClassName}>
                <CFormLabel>Products {requiredIcon}</CFormLabel>
            </div>
            <div className="w-100">
                <AsyncPaginate
                    className={`${(error && required) ? 'multiSelect_error' : ''}`}
                    value={allSelected ? value.filter((option) => option.value !== 'select_all') : value}
                    loadOptions={fetchProducts}
                    isMulti={multiSelect}
                    closeMenuOnSelect={!multiSelect}
                    onChange={handleOnChange}
                    placeholder="Select product..."
                    noOptionsMessage={() => 'No product found'}
                    loadingMessage={() => 'Product Loading...'}
                    additional={{ totalRenderedRecords }}
                    SelectProps={{ isSearchable: true }}
                    menuShouldScrollIntoView={false}
                    menuPortalTarget={document.querySelector('body')}
                    styles={customStyles}
                />
                {(error && required) && <span className="text-danger error_message">{multiSelect ? 'Please select one or more product(s)' : 'Please select a product.'}</span>}
            </div>
        </div>
    );
}

export default ProductSelection;
